import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { LangProvider } from "@/i18n";
import { SiteLayout } from "@/components/SiteLayout";
import { ImageLightbox } from "@/components/ImageLightbox";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ROUTES } from "@/config/links";
import Home from "@/pages/Home";
import Landing from "@/pages/Landing";
import About from "@/pages/About";
import HowWeWork from "@/pages/HowWeWork";
import ExecutiveCoaching from "@/pages/ExecutiveCoaching";
import CorporateTraining from "@/pages/CorporateTraining";
import LDPartnerships from "@/pages/LDPartnerships";
import BookingSelect from "@/pages/BookingSelect";
import BookingPresentation from "@/pages/BookingPresentation";
import BookingExecutivePresence from "@/pages/BookingExecutivePresence";
import BookingCorporate from "@/pages/BookingCorporate";
import BookingLdPartnership from "@/pages/BookingLdPartnership";
import Contact from "@/pages/Contact";
import PresentationReadinessDiagnostic from "@/pages/PresentationReadinessDiagnostic";
import NextPresentationReadinessCheck from "@/pages/NextPresentationReadinessCheck";
import ExecutivePresenceCommunicationDiagnostic from "@/pages/ExecutivePresenceCommunicationDiagnostic";
import PresenterArchetype from "@/pages/PresenterArchetype/PresenterArchetype";
import ExecutivePresenceDevelopment from "@/pages/ExecutivePresenceDevelopment";
import PresentationExcellence from "@/pages/PresentationExcellence";
import ExecutivePresenceForLeaders from "@/pages/ExecutivePresenceForLeaders";
import CustomPrograms from "@/pages/CustomPrograms";
import { ArticlesList, ArticleDetail, CaseStudiesList, CaseStudyDetail, ToolsList, GuidesList } from "@/pages/Insights";
import PresenterPlaybookGuide from "@/pages/PresenterPlaybookGuide/PresenterPlaybookGuide";
import InsightsAdmin from "@/pages/InsightsAdmin";
import AdminMarketing from "@/pages/AdminMarketing";
import HighStakesAudit from "@/pages/HighStakesAudit";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import ComingSoon from "@/pages/ComingSoon";
import ExerciseHub from "@/pages/ExerciseHub/ExerciseHub";
import ReportAccess from "@/pages/ReportAccess/ReportAccess";
import RehearsalRoomApp from "@/pages/RehearsalRoom/RehearsalRoomApp";
import WorkplaceCommunicationResearch from "@/pages/WorkplaceCommunicationResearch";
import { Toaster } from "@/components/ui/sonner";

/**
 * Every route (Home, About, Coaching, …) is defined once inside <PageRoutes/>
 * and mounted three times — once at "/", once at "/pt/*" and once at "/el/*".
 * The corresponding <LangProvider> injects the active language so useT(),
 * useL() and useLang() resolve correctly inside every page.
 */
const PageRoutes = () => (
  <Routes>
    {/* Standalone immersive route — bypasses SiteLayout entirely */}
    <Route path="insights/diagnostics/high-stakes-audit" element={<HighStakesAudit />} />
    <Route path="exercise/:slug" element={<ExerciseHub />} />
    <Route path="report/:token" element={<ReportAccess />} />
    <Route
      path="insights/diagnostics/executive-presence-communication-diagnostic"
      element={<ExecutivePresenceCommunicationDiagnostic />}
    />
    <Route
      path="insights/diagnostics/next-presentation-readiness-check"
      element={<NextPresentationReadinessCheck />}
    />
    <Route path="insights/diagnostics/presenter-archetype" element={<PresenterArchetype />} />

    <Route element={<SiteLayout />}>
      {/* Home */}
      <Route index element={<Home />} />

      {/* Executive Coaching pillar */}
      <Route path="executive-coaching" element={<ExecutiveCoaching />} />
      <Route
        path="executive-coaching/high-stakes-presentation-coaching"
        element={<Landing />}
      />
      <Route
        path="executive-coaching/executive-presence-development"
        element={<ExecutivePresenceDevelopment />}
      />

      {/* Corporate Training pillar */}
      <Route path="corporate-training" element={<CorporateTraining />} />
      <Route
        path="corporate-training/presentation-excellence"
        element={<PresentationExcellence />}
      />
      <Route
        path="corporate-training/executive-presence-for-leaders"
        element={<ExecutivePresenceForLeaders />}
      />
      <Route
        path="corporate-training/custom-programs"
        element={<CustomPrograms />}
      />

      {/* L&D Partnerships */}
      <Route path="ld-partnerships" element={<LDPartnerships />} />

      {/* About */}
      <Route path="about" element={<About />} />

      {/* Booking + Contact */}
      <Route path="book" element={<BookingSelect />} />
      <Route path="book/presentation" element={<BookingPresentation />} />
      <Route path="book/executive-presence" element={<BookingExecutivePresence />} />
      <Route path="book/corporate" element={<BookingCorporate />} />
      <Route path="book/ld-partnership" element={<BookingLdPartnership />} />
      {/* Old booking route — preserved as a redirect to the Presentation
          Coaching booking page, which inherited its original calendar
          integration (same TidyCal booking type as before). */}
      <Route path="booking" element={<Navigate to="../book/presentation" replace />} />
      <Route path="contact" element={<Contact />} />

      {/* Insights hub (Phase 4) */}
      <Route
        path="insights"
        element={<ComingSoon title="Insights" titleKey="meta.insights_title" descriptionKey="meta.insights_description" path={ROUTES.insights} />}
      />
      <Route
        path="insights/free-presentation-assessment"
        element={<Navigate to="../insights/diagnostics/next-presentation-readiness-check" replace />}
      />
      <Route
        path="insights/diagnostics/presentation-readiness"
        element={<PresentationReadinessDiagnostic />}
      />
      <Route
        path="insights/diagnostics"
        element={<ComingSoon title="Diagnostics" titleKey="meta.diagnostics_title" descriptionKey="meta.diagnostics_description" path={ROUTES.diagnostics} />}
      />
      <Route path="insights/articles" element={<ArticlesList />} />
      <Route path="insights/articles/:slug" element={<ArticleDetail />} />
      <Route path="insights/case-studies" element={<CaseStudiesList />} />
      <Route path="insights/case-studies/:slug" element={<CaseStudyDetail />} />
      <Route path="insights/guides" element={<GuidesList />} />
      <Route path="insights/guides/next-presentation-playbook" element={<PresenterPlaybookGuide />} />
      <Route path="insights/tools" element={<ToolsList />} />
      <Route path="resources/workplace-communication-research" element={<WorkplaceCommunicationResearch />} />

      {/* Insights CMS admin */}
      <Route path="admin/insights" element={<InsightsAdmin />} />
      <Route path="admin/marketing" element={<AdminMarketing />} />

      {/* Legal */}
      <Route path="privacy" element={<Privacy />} />
      <Route path="terms" element={<Terms />} />

      {/* Methodology */}
      <Route path="how-we-work" element={<HowWeWork />} />

      {/* WordPress → React redirects */}
      <Route path="book-a-discovery-call" element={<Navigate to="../book/presentation" replace />} />
      <Route
        path="free-diagnostic"
        element={<Navigate to="../insights/diagnostics/next-presentation-readiness-check" replace />}
      />

      {/* 404 → Home (relative so the language prefix is preserved) */}
      <Route path="*" element={<Navigate to="." replace />} />
    </Route>
  </Routes>
);

function App() {
  return (
    <MotionConfig reducedMotion="user">
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Rehearsal Room — standalone authenticated app boundary, not part
                of the i18n marketing-site route tree, not tripled per language. */}
            <Route path="/rehearsal-room/*" element={<RehearsalRoomApp />} />
            <Route
              path="/pt/*"
              element={
                <LangProvider lang="pt">
                  <PageRoutes />
                </LangProvider>
              }
            />
            <Route
              path="/el/*"
              element={
                <LangProvider lang="el">
                  <PageRoutes />
                </LangProvider>
              }
            />
            <Route
              path="/*"
              element={
                <LangProvider lang="en">
                  <PageRoutes />
                </LangProvider>
              }
            />
          </Routes>
          {/* Global image lightbox — makes editorial sketches & photos clickable */}
          <ImageLightbox />
          {/* Global toast notifications (sonner) — every toast()/toast.error()/
              toast.info() call across the app, including the Rehearsal Room
              Files feature, needs this mounted exactly once. */}
          <Toaster position="top-center" richColors data-testid="global-toaster" />
        </BrowserRouter>
      </div>
    </MotionConfig>
  );
}

export default App;
